/*
  Trauti product gallery: thumb-to-hero binding, hover-zoom, lightbox.
  Exposes pure helpers for testability (nextIndex / prevIndex).
*/

(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  } else {
    root.TrautisMediaLightbox = api;
    if (root.document && root.document.addEventListener) {
      if (root.document.readyState === 'loading') {
        root.document.addEventListener('DOMContentLoaded', api.init);
      } else {
        api.init();
      }
    }
  }
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  // ===== Pure helpers (testable) ===========================================

  function nextIndex(current, total) {
    if (total <= 1) return 1;
    return current >= total ? 1 : current + 1;
  }

  function prevIndex(current, total) {
    if (total <= 1) return 1;
    return current <= 1 ? total : current - 1;
  }

  // Indices to keep "warm" around the active slide: the slide itself plus its
  // wrap-around neighbours, so navigating never lands on an un-fetched image.
  // Returns 1-based, de-duped (collapses to fewer entries when total <= 2).
  function neighborIndices(idx, total) {
    if (total <= 1) return [1];
    var set = [idx, nextIndex(idx, total), prevIndex(idx, total)];
    return set.filter(function (v, i) { return set.indexOf(v) === i; });
  }

  // ===== Preloading =========================================================
  // Root cause of "images load slowly when swiping": every non-first slide is
  // loading="lazy" and (on desktop) sits in a display:none slide, so the browser
  // defers its fetch until the moment it becomes active — a cold fetch on every
  // navigation. Flipping lazy→eager starts the fetch immediately, even while the
  // slide is hidden. Quality is untouched: same srcset/src, only earlier timing.
  function warmImage(slide) {
    if (!slide) return;
    var img = slide.querySelector('img[loading="lazy"]');
    if (img) img.loading = 'eager';
  }

  // Warm the active slide + its neighbours within a slide list whose index lives
  // in `attr` (data-media-idx for the inline gallery, data-lb-idx in lightbox).
  function warmNeighbors(slides, idx, total, attr) {
    if (!slides || !slides.length) return;
    neighborIndices(idx, total).forEach(function (n) {
      for (var i = 0; i < slides.length; i++) {
        if (parseInt(slides[i].getAttribute(attr), 10) === n) { warmImage(slides[i]); break; }
      }
    });
  }

  // ===== Existing logic (UNCHANGED — paste functions verbatim) =============

  // ---- Thumb click → swap active slide -------------------------------------
  function bindGallery(root) {
    var thumbs = root.querySelectorAll('[data-thumb-idx]');
    var slides = root.querySelectorAll('[data-media-idx]');
    if (!slides.length) return null;

    function setActive(idx) {
      thumbs.forEach(function (t) {
        var ti = parseInt(t.getAttribute('data-thumb-idx'), 10);
        t.classList.toggle('is-active', ti === idx);
      });
      slides.forEach(function (s) {
        var si = parseInt(s.getAttribute('data-media-idx'), 10);
        s.classList.toggle('is-active', si === idx);
      });
      warmNeighbors(slides, idx, slides.length, 'data-media-idx');
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function (e) {
        e.preventDefault();
        // Overflow slot: open lightbox at slot's media index (Desktop only)
        var overflowTrigger = thumb.getAttribute('data-overflow-trigger');
        if (overflowTrigger && !isMobileCarousel()) {
          openLightbox(root, parseInt(overflowTrigger, 10));
          return;
        }
        var idx = parseInt(thumb.getAttribute('data-thumb-idx'), 10);
        if (isMobileCarousel()) {
          scrollHeroTo(root, idx);
        } else {
          setActive(idx);
        }
      });
    });

    // Warm the neighbours of the starting slide so the very first navigation
    // (before any setActive call) is already instant.
    warmNeighbors(slides, getActiveIndex(slides), slides.length, 'data-media-idx');

    return { setActive: setActive, total: slides.length, slides: slides, thumbs: thumbs };
  }

  function getActiveIndex(slides) {
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].classList.contains('is-active')) {
        return parseInt(slides[i].getAttribute('data-media-idx'), 10);
      }
    }
    return 1;
  }

  function bindNav(root, gallery) {
    if (!gallery) return;
    var prevBtn = root.querySelector('[data-nav-prev]');
    var nextBtn = root.querySelector('[data-nav-next]');
    if (!prevBtn && !nextBtn) return;

    function step(direction) {
      var current = getActiveIndex(gallery.slides);
      var next = direction === 'next'
        ? nextIndex(current, gallery.total)
        : prevIndex(current, gallery.total);
      if (isMobileCarousel()) {
        var wrapping = (direction === 'next' && current === gallery.total && next === 1) ||
                       (direction === 'prev' && current === 1 && next === gallery.total);
        if (wrapping) wrapHero(root, next);
        else scrollHeroTo(root, next);
      } else {
        gallery.setActive(next);
      }
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { step('prev'); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step('next'); });
  }

  function isMobileCarousel() {
    return typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(max-width: 989px)').matches;
  }

  function bindMobileSync(root, gallery) {
    if (!gallery) return;
    var hero = root.querySelector('.t-media__hero');
    var dots = root.querySelectorAll('[data-dot-idx]');
    var counter = root.querySelector('[data-media-counter]');
    if (!hero || !gallery.slides.length) return;

    var rafScheduled = false;
    function onScroll() {
      if (rafScheduled) return;
      rafScheduled = true;
      requestAnimationFrame(function () {
        rafScheduled = false;
        if (!isMobileCarousel()) return;
        var heroLeft = hero.scrollLeft;
        var width = hero.clientWidth;
        if (width === 0) return;
        var idx = Math.round(heroLeft / width) + 1;
        if (idx < 1) idx = 1;
        if (idx > gallery.total) idx = gallery.total;
        syncActive(idx);
      });
    }
    function syncActive(idx) {
      gallery.thumbs.forEach(function (t) {
        var ti = parseInt(t.getAttribute('data-thumb-idx'), 10);
        t.classList.toggle('is-active', ti === idx);
      });
      gallery.slides.forEach(function (s) {
        var si = parseInt(s.getAttribute('data-media-idx'), 10);
        s.classList.toggle('is-active', si === idx);
      });
      dots.forEach(function (d) {
        var di = parseInt(d.getAttribute('data-dot-idx'), 10);
        d.classList.toggle('is-active', di === idx);
      });
      if (counter) counter.textContent = String(idx).padStart(2, '0') + ' / ' + gallery.total;
      warmNeighbors(gallery.slides, idx, gallery.total, 'data-media-idx');
    }
    hero.addEventListener('scroll', onScroll, { passive: true });
  }

  // ---- Mobile swipe loop: wrap when swiping past the first/last slide -------
  // Native scroll-snap can't scroll beyond the edges, so a swipe at the first/
  // last image just dead-ends. Detect that blocked edge-swipe and jump to the
  // other end (mirrors the wrap the arrow buttons already do via bindNav).
  function bindMobileLoop(root, gallery) {
    if (!gallery) return;
    var hero = root.querySelector('.t-media__hero');
    if (!hero) return;
    var startX = null;
    var startScroll = 0;
    hero.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { startX = null; return; }
      startX = e.touches[0].clientX;
      startScroll = hero.scrollLeft;
    }, { passive: true });
    hero.addEventListener('touchend', function (e) {
      if (startX === null || !isMobileCarousel()) { startX = null; return; }
      var dx = e.changedTouches[0].clientX - startX;
      startX = null;
      if (Math.abs(dx) < 45) return;
      // Gate on the edge we were on when the swipe STARTED, not on whether the
      // scroll moved: native rubber-banding/inertia on real devices leaves
      // scrollLeft slightly off the edge at touchend, which a "did it move?"
      // check misreads. A swipe that begins at an edge and pushes further past
      // it = a wrap; mid-carousel swipes start away from the edge → native scroll.
      var maxScroll = hero.scrollWidth - hero.clientWidth;
      if (dx < 0 && startScroll >= maxScroll - 2) {
        wrapHero(root, 1);
      } else if (dx > 0 && startScroll <= 2) {
        wrapHero(root, gallery.total);
      }
    }, { passive: true });
  }

  // A loop wrap (last↔first) can't scroll smoothly without sliding through every
  // slide, so it jumps instantly — which reads as jerky next to the smooth native
  // swipes. Mask the jump with a quick crossfade: fade out, jump while hidden,
  // fade back in.
  function wrapHero(root, idx) {
    var hero = root.querySelector('.t-media__hero');
    if (!hero) return;
    hero.style.transition = 'opacity 140ms ease';
    hero.style.opacity = '0';
    window.setTimeout(function () {
      scrollHeroTo(root, idx, { instant: true });
      window.requestAnimationFrame(function () { hero.style.opacity = '1'; });
    }, 150);
    window.setTimeout(function () {
      hero.style.transition = '';
      hero.style.opacity = '';
    }, 360);
  }

  function scrollHeroTo(root, idx, opts) {
    var hero = root.querySelector('.t-media__hero');
    if (!hero) return;
    var slide = hero.querySelector('[data-media-idx="' + idx + '"]');
    if (!slide) return;
    var behavior = (opts && opts.instant) ? 'auto' : 'smooth';
    slide.scrollIntoView({ behavior: behavior, inline: 'start', block: 'nearest' });
  }

  // ---- Hover-zoom (desktop) -----------------------------------------------
  function bindHoverZoom(root) {
    if (window.matchMedia && window.matchMedia('(hover: none)').matches) return;
    var imgs = root.querySelectorAll('.t-media__img');
    imgs.forEach(function (img) {
      var slide = img.closest('.t-media__slide');
      if (!slide) return;
      slide.addEventListener('mousemove', function (e) {
        var rect = slide.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width) * 100;
        var y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
      });
      slide.addEventListener('mouseenter', function () {
        slide.classList.add('is-zooming');
      });
      slide.addEventListener('mouseleave', function () {
        slide.classList.remove('is-zooming');
        img.style.transformOrigin = '50% 50%';
      });
    });
  }

  // ---- Adaptive backdrop --------------------------------------------------
  // Schwarze/transparente PNGs verschwinden auf dem dunklen Default-Backdrop.
  // Wir messen die mittlere Helligkeit der *opaken* Pixel (transparente werden
  // ignoriert, da dort eh der Backdrop durchscheint) und wählen den Backdrop:
  //   dunkles Motiv  → heller Backdrop
  //   helles Motiv   → dunkler Backdrop (Default)
  // Rückgabe: 'light' | 'dark' | null (CORS-getaintet oder vollständig transparent).
  function classifyBrightness(img) {
    try {
      var w = 24, h = 24;
      var canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      var ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, w, h);
      var data = ctx.getImageData(0, 0, w, h).data;
      var sum = 0, opaque = 0;
      for (var i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 40) continue; // (nahezu) transparent → überspringen
        sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
        opaque++;
      }
      if (opaque === 0) return null;
      // Backdrop wird heller getönt; Schwelle daher etwas unter Mitte, damit
      // klar dunkle Motive den hellen Backdrop bekommen.
      return (sum / opaque) < 120 ? 'light' : 'dark';
    } catch (e) {
      return null; // getaintetes Canvas (kein CORS) → Default-Backdrop behalten
    }
  }

  // ---- Lightbox (click hero → fullscreen modal) ----------------------------
  function bindLightbox(root) {
    var slides = root.querySelectorAll('.t-media__slide');
    slides.forEach(function (slide) {
      slide.addEventListener('click', function (e) {
        if (e.target.closest('.t-media__video')) return; // video controls
        var idx = parseInt(slide.getAttribute('data-media-idx'), 10);
        openLightbox(root, idx);
      });
    });
  }

  function openLightbox(root, startIdx) {
    var slides = root.querySelectorAll('.t-media__slide');
    if (!slides.length) return;
    var total = slides.length;
    var current = startIdx;

    var modal = document.createElement('div');
    modal.className = 't-lightbox';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Produktbild');

    var backdrop = document.createElement('div');
    backdrop.className = 't-lightbox__backdrop';
    backdrop.addEventListener('click', close);

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 't-lightbox__close';
    closeBtn.setAttribute('aria-label', 'Schließen');
    closeBtn.textContent = '✕';
    closeBtn.addEventListener('click', close);

    var content = document.createElement('div');
    content.className = 't-lightbox__content';

    var lightboxSlides = [];
    slides.forEach(function (slide) {
      var lbSlide = document.createElement('div');
      lbSlide.className = 't-lightbox__slide';
      lbSlide.setAttribute('data-lb-idx', slide.getAttribute('data-media-idx'));

      var type = slide.getAttribute('data-media-type');
      if (type === 'video' || type === 'external_video') {
        var srcNode = slide.querySelector('video, iframe');
        if (srcNode) lbSlide.appendChild(srcNode.cloneNode(true));
      } else {
        var img = slide.querySelector('.t-media__img');
        if (img) {
          var big = document.createElement('img');
          big.className = 't-lightbox__img';
          // crossOrigin VOR src setzen, damit das Canvas (Helligkeitsmessung)
          // nicht getaintet wird. Shopify-CDN liefert ACAO:* — daher unkritisch.
          big.crossOrigin = 'anonymous';
          big.alt = img.alt;
          big.loading = 'lazy';
          big.addEventListener('load', function (lb) {
            return function () {
              var mode = classifyBrightness(this);
              if (mode) lb.dataset.bright = mode;
              applyBackdrop();
            };
          }(lbSlide));
          big.src = img.getAttribute('data-zoom-src') || img.src;
          lbSlide.appendChild(big);
        }
      }
      content.appendChild(lbSlide);
      lightboxSlides.push(lbSlide);
    });

    function chevronSvg(d) {
      var SVG_NS = 'http://www.w3.org/2000/svg';
      var svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      var path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', d);
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '2.5');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      path.setAttribute('fill', 'none');
      svg.appendChild(path);
      return svg;
    }

    var prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 't-lightbox__nav t-lightbox__nav--prev';
    prevBtn.setAttribute('aria-label', 'Vorheriges Bild');
    prevBtn.appendChild(chevronSvg('M15 6l-6 6 6 6'));
    prevBtn.addEventListener('click', function () { go(prevIndex(current, total)); });

    var nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 't-lightbox__nav t-lightbox__nav--next';
    nextBtn.setAttribute('aria-label', 'Nächstes Bild');
    nextBtn.appendChild(chevronSvg('M9 6l6 6-6 6'));
    nextBtn.addEventListener('click', function () { go(nextIndex(current, total)); });

    var counter = document.createElement('div');
    counter.className = 't-lightbox__counter';

    modal.appendChild(backdrop);
    modal.appendChild(closeBtn);
    modal.appendChild(content);
    if (total > 1) {
      modal.appendChild(prevBtn);
      modal.appendChild(nextBtn);
      modal.appendChild(counter);
    }

    document.body.appendChild(modal);
    document.body.classList.add('t-lightbox-open');
    document.addEventListener('keydown', onKey);

    go(current);

    // Touch swipe
    var touchStartX = null;
    var touchStartY = null;
    content.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { touchStartX = null; return; }
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    content.addEventListener('touchend', function (e) {
      if (touchStartX === null) return;
      var t = e.changedTouches[0];
      var dx = t.clientX - touchStartX;
      var dy = t.clientY - touchStartY;
      touchStartX = null;
      // Ignore vertical swipes and small movements
      if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) return;
      go(dx < 0 ? nextIndex(current, total) : prevIndex(current, total));
    }, { passive: true });

    // Backdrop an das aktuell sichtbare Bild anpassen (s. classifyBrightness).
    function applyBackdrop() {
      var active = null;
      for (var i = 0; i < lightboxSlides.length; i++) {
        if (parseInt(lightboxSlides[i].getAttribute('data-lb-idx'), 10) === current) {
          active = lightboxSlides[i];
          break;
        }
      }
      modal.classList.toggle('t-lightbox--light', !!(active && active.dataset.bright === 'light'));
    }

    function go(idx) {
      current = idx;
      lightboxSlides.forEach(function (s) {
        var si = parseInt(s.getAttribute('data-lb-idx'), 10);
        s.classList.toggle('is-active', si === idx);
        if (si !== idx) {
          var v = s.querySelector('video');
          if (v) try { v.pause(); } catch (e) {}
        }
      });
      counter.textContent = idx + ' von ' + total;
      warmNeighbors(lightboxSlides, idx, total, 'data-lb-idx');
      applyBackdrop();
    }

    function close() {
      document.removeEventListener('keydown', onKey);
      if (modal.parentNode) modal.parentNode.removeChild(modal);
      document.body.classList.remove('t-lightbox-open');
    }
    function onKey(e) {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') go(prevIndex(current, total));
      else if (e.key === 'ArrowRight') go(nextIndex(current, total));
    }
  }

  // ===== Init ==============================================================

  function init() {
    document.querySelectorAll('[data-product-media]').forEach(function (root) {
      if (root.dataset.bound === '1') return;
      root.dataset.bound = '1';
      var gallery = bindGallery(root);
      bindNav(root, gallery);
      bindMobileSync(root, gallery);
      bindMobileLoop(root, gallery);
      bindHoverZoom(root);
      bindLightbox(root);
    });
  }

  return {
    init: init,
    nextIndex: nextIndex,
    prevIndex: prevIndex,
    neighborIndices: neighborIndices
  };
});
